import userActivityTrack from "@plugins/user-activity-track/index.ts";
import {
  ApiContext,
  ApiEvent,
  ClickSearchResultContext,
  ClickSearchResultEvent,
  ConnectionAction,
  ConnectionEvent,
  CspViolationContext,
  CspViolationEvent,
  EmbedContext,
  EmbedEvent,
  IotContext,
  IotEvent,
  LaunchAppContext,
  LaunchAppEvent,
  LogoutEvent,
  MoeBannerClickEvent,
  PageViewEvent,
  PasteEventData,
  RealtimeSessionEvent,
  ResourceActionContext,
  ResourceActionEvent,
  SearchContext,
  SearchEvent,
} from "@plugins/user-activity-track/types.ts";
import store from "@plugins/store.ts";
import { appSessionStorage } from "@utils/app.storage";
import { CONNECTION_EVENT_KEY } from "@plugins/user-activity-track/service/user.activity.track.constant.ts";
import { isEmpty } from "@/utils/var.check";

interface EmbedData {
  embedType?: string; // embed type: "embed-app" | "embed-url" | "embed-code"
  embedUrl?: string; // URL of the embedded resource
  lessonId?: string;
  lessonType?: string;
  lessonResourceDomain?: string;
  classGroupId?: string;
  appUuid?: string; // app uuid — only for embed-app type
}

interface IotData {
  teamId?: string;
  assignmentId?: string;
  postUuid?: string;
  topicId?: string;
  details?: Record<string, unknown>;
  classGroupId?: string;
  event: string;
  connectionId: string;
  url: string;
}

interface ApiData {
  classGroupId?: string;
  httpStatus: number;
  url: string;
  queryMap?: Record<string, unknown>;
}

interface ResourceActionData {
  admin?: boolean;
  context?: {
    classGroupId?: string;
    [key: string]: unknown;
  };
  action: string;
  objectType: string;
  objectId: string;
  pathMap?: Record<string, unknown>;
}

interface ConnectionStatusData {
  action: ConnectionAction;
}

interface SearchData {
  searchSessionId: string;
  resultId: string;
  keyword: string;
  resource: string;
  subject: string;
  level: string;
  location: string;
  resourceType: string;
  authors: string;
  syllabus: string;
  topic: string;
  parentTopic: string;
  learningOutcome: string;
  total: number;
}

interface ClickSearchResultData {
  objectId: string;
  objectType: string;
  searchSessionId: string;
  resultId: string;
  resultPageNo: number;
  resultRank: number;
}

interface LaunchAppData {
  appUuid: string;
  appName: string;
  classGroupId?: string; // only when launching installed app in class group
}

interface ConnectionStatusData {
  action: ConnectionAction;
}

interface CspViolationData {
  effectiveDirective: string;
  blockedUri?: string;
  sourceFile?: string;
}

// Helper function to safely get current page URL
const getCurrentPageUrl = (): string => {
  if (typeof window !== "undefined" && window.location) {
    return window.location.href;
  }
  return "";
};

const getCurrentPath = (): string => {
  if (typeof window !== "undefined" && window.location) {
    return window.location.pathname;
  }
  return "";
};

const getCurrentQueryMap = (): Record<string, string> => {
  if (typeof window === "undefined" || !window.location) {
    return {};
  }

  const queryMap: Record<string, string> = {};
  const queryParams = new URLSearchParams(window.location.search);
  queryParams.forEach((value, key) => {
    queryMap[key] = value;
  });

  return queryMap;
};

// Helper function to filter out undefined values from objects
const filterUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
};

// Helper functions to create context objects
const createEmbedContext = (data: EmbedData): EmbedContext => {
  const context = {
    lessonId: data.lessonId,
    lessonType: data.lessonType,
    lessonDomain: data.lessonResourceDomain,
    pageUrl: getCurrentPageUrl(),
    classGroupId: data.classGroupId,
    appUuid: data.appUuid, // filtered out when undefined
  };
  return filterUndefined(context);
};

const createIotContext = (data: IotData): IotContext => {
  const context = {
    teamId: data.teamId,
    assignmentId: data.assignmentId,
    postUuid: data.postUuid,
    topicId: data.topicId,
    details: data.details,
    pageUrl: getCurrentPageUrl(),
    classGroupId: data.classGroupId,
  };
  return filterUndefined(context);
};

const createApiContext = (data: ApiData): ApiContext => {
  const context = {
    pageUrl: getCurrentPageUrl(),
    classGroupId: data.classGroupId,
  };
  return filterUndefined(context);
};

const createResourceActionContext = (data: ResourceActionData): ResourceActionContext => {
  const context = {
    admin: data.admin,
    classGroupId: data.context?.classGroupId,
    ...data.context,
  };
  return filterUndefined(context);
};

const createSearchContext = (data: SearchData): SearchContext => {
  return {
    searchSessionId: data.searchSessionId,
    resultId: data.resultId,
    keyword: data.keyword,
    resource: data.resource,
    subject: data.subject,
    level: data.level,
    location: data.location,
    resourceType: data.resourceType,
    authors: data.authors,
    syllabus: data.syllabus,
    topic: data.topic,
    parentTopic: data.parentTopic,
    learningOutcome: data.learningOutcome,
    total: data.total,
  };
};

const createClickSearchResultContext = (data: ClickSearchResultData): ClickSearchResultContext => {
  return {
    searchSessionId: data.searchSessionId,
    resultId: data.resultId,
    resultPageId: data.resultPageNo,
    resultRankId: data.resultRank,
  };
};

const createLaunchAppContext = (data: LaunchAppData): LaunchAppContext => {
  return filterUndefined({
    appName: data.appName,
    classGroupId: data.classGroupId,
  }) as LaunchAppContext;
};

const createCspViolationContext = (data: CspViolationData): CspViolationContext => {
  return filterUndefined({
    blockedUri: data.blockedUri,
    sourceFile: data.sourceFile,
  }) as CspViolationContext;
};

export default {
  pageView(to) {
    const pageViewData = {
      action: "pageview",
      objectType: "url",
      objectId: to.path,
      url: to.path,
      queryMap: to.query,
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(pageViewData as PageViewEvent);
  },
  redirectPage(from) {
    const pageViewData = {
      action: "redirect",
      objectType: "url",
      objectId: from.path,
      url: from.path,
      queryMap: from.query,
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(pageViewData as PageViewEvent);
  },
  invalidUrl(from) {
    const pageViewData = {
      action: "invalid-url",
      objectType: "url",
      objectId: from.path,
      url: from.path,
      queryMap: from.query,
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(pageViewData as PageViewEvent);
  },
  resourceAction(data, to) {
    const resourceActionEventData = {
      action: data.action,
      objectType: data.objectType,
      objectId: data.objectId,
      pathMap: data.pathMap,
      queryMap: to.query,
      context: createResourceActionContext(data),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(resourceActionEventData as ResourceActionEvent);
  },
  api(data) {
    const apiEventData = {
      httpStatus: data.httpStatus,
      action: "api",
      objectType: "url",
      objectId: data.url,
      url: data.url,
      queryMap: data.queryMap,
      timestamp: new Date(store.state.app.serverTime).toISOString(),
      context: createApiContext(data),
      responseTime: data.responseTime,
    };
    userActivityTrack.track(apiEventData as ApiEvent);
  },
  iot(data) {
    const iotEventData = {
      action: data.event,
      objectType: "connection-id",
      objectId: data.connectionId,
      connectionId: data.connectionId,
      url: data.url,
      timestamp: new Date(store.state.app.serverTime).toISOString(),
      context: createIotContext(data),
      pageUrl: window.location.href,
      teamId: data.teamId,
      assignmentId: data.assignmentId,
      topicId: data.topicId,
      postUuid: data.postUuid,
      details: data.details,
    };
    userActivityTrack.track(iotEventData as IotEvent);
  },
  embed(data) {
    const embedData = {
      action: "embed",
      objectType: data.embedType,
      objectId: data.embedUrl,
      embedUrl: data.embedUrl,
      timestamp: new Date(store.state.app.serverTime).toISOString(),
      context: createEmbedContext(data),
      pageUrl: window.location.href,
      lessonId: data.lessonId,
      lessonType: data.lessonType,
      lessonResourceDomain: data.lessonResourceDomain,
    };
    userActivityTrack.track(embedData as EmbedEvent);
  },
  logout() {
    const logoutData = {
      action: "logout",
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(logoutData as LogoutEvent);
  },
  connectionStatus(data: ConnectionStatusData) {
    if (!store.state.auth.logged) return;

    const connectionEventData: Partial<ConnectionEvent> = {
      action: data.action,
      objectType: "connection",
      objectId: "",
      url: getCurrentPath(),
      queryMap: getCurrentQueryMap(),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };

    const connectionStorageValue = appSessionStorage.getItem(CONNECTION_EVENT_KEY);
    const connectionEvents: Partial<ConnectionEvent>[] = isEmpty(connectionStorageValue)
      ? []
      : (JSON.parse(connectionStorageValue) as Partial<ConnectionEvent>[]);

    connectionEvents.push(connectionEventData);

    if (data.action === "disconnect") {
      appSessionStorage.setItem(CONNECTION_EVENT_KEY, connectionEvents);
    } else if (data.action === "reconnect") {
      appSessionStorage.removeItem(CONNECTION_EVENT_KEY);
      connectionEvents.forEach((event: Partial<ConnectionEvent>) => {
        userActivityTrack.track(event as ConnectionEvent);
      });
    } else {
      userActivityTrack.track(connectionEventData as ConnectionEvent);
    }
  },
  flushConnectionEvents() {
    if (!store.state.auth.logged) {
      appSessionStorage.removeItem(CONNECTION_EVENT_KEY);
      return;
    }

    const connectionStorageValue = appSessionStorage.getItem(CONNECTION_EVENT_KEY);
    const connectionEvents: Partial<ConnectionEvent>[] = isEmpty(connectionStorageValue)
      ? []
      : (JSON.parse(connectionStorageValue) as Partial<ConnectionEvent>[]);
    if (!isEmpty(connectionEvents)) {
      appSessionStorage.removeItem(CONNECTION_EVENT_KEY);

      // Add a reconnect event to indicate the user has come back online
      const reconnectEventData: Partial<ConnectionEvent> = {
        action: "reconnect",
        objectType: "connection",
        objectId: "",
        url: getCurrentPath(),
        queryMap: getCurrentQueryMap(),
        timestamp: new Date(store.state.app.serverTime).toISOString(),
      };
      connectionEvents.push(reconnectEventData);

      connectionEvents.forEach((event: Partial<ConnectionEvent>) => {
        userActivityTrack.track(event as ConnectionEvent);
      });
    }
  },
  search(data: SearchData) {
    const searchEventData = {
      action: "search",
      context: createSearchContext(data),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(searchEventData as SearchEvent);
  },
  clickSearchResult(data: ClickSearchResultData) {
    const clickSearchResultEventData = {
      action: "click-search-result",
      objectId: data.objectId,
      objectType: data.objectType,
      context: createClickSearchResultContext(data),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(clickSearchResultEventData as ClickSearchResultEvent);
  },
  cspViolation(data: CspViolationData) {
    const cspViolationEventData = {
      action: "csp-violate",
      objectType: "csp-directive",
      objectId: data.effectiveDirective,
      pageUrl: getCurrentPageUrl(),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
      context: createCspViolationContext(data),
    };
    userActivityTrack.track(cspViolationEventData as CspViolationEvent);
  },
  launchApp(data: LaunchAppData) {
    const launchAppEventData = {
      action: "launch-app",
      objectType: "app",
      objectId: data.appUuid,
      pageUrl: getCurrentPageUrl(),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
      context: createLaunchAppContext(data),
    };
    userActivityTrack.track(launchAppEventData as LaunchAppEvent);
  },
  paste(data: {
    activityUuid: string;
    assignmentUuid?: string;
    editorId: string;
    moduleUuid: string;
    pastedContent: string;
    pastedContentLength: number;
    questionUuid: string;
    questionType: string;
    sectionUuid: string;
    location: string;
  }) {
    const pasteEventData = {
      action: "paste",
      objectType: "editor",
      objectId: data.editorId,
      pageUrl: getCurrentPageUrl(),
      context: filterUndefined({
        pastedContent: data.pastedContent,
        pastedContentLength: data.pastedContentLength,
        moduleId: data.moduleUuid,
        assignmentId: data.assignmentUuid,
        sectionId: data.sectionUuid,
        activityId: data.activityUuid,
        questionId: data.questionUuid,
        questionType: data.questionType,
        location: data.location,
      }),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(pasteEventData as PasteEventData);
  },
  moeBannerClicked(data: { levelGroup: string; levelId?: string | number; subjectId?: number }) {
    const moeBannerClickEvent = {
      action: "moe-banner-click",
      objectType: "moe-banner",
      objectId: data.levelGroup,
      pageUrl: getCurrentPageUrl(),
      context: filterUndefined({
        levelId: data.levelId,
        subjectId: data.subjectId,
      }),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(moeBannerClickEvent as MoeBannerClickEvent);
  },
  realtimeSession(data: {
    action: string;
    clientCorrelationId: string;
    resourceUuid: string;
    isNewSession?: boolean;
    openaiSessionId?: string;
    errorMessage?: string;
    agentName?: string;
    transport?: string;
  }) {
    const realtimeSessionEventData = {
      action: data.action,
      objectType: "post",
      objectId: data.resourceUuid,
      url: getCurrentPath(),
      pageUrl: getCurrentPageUrl(),
      context: filterUndefined({
        clientCorrelationId: data.clientCorrelationId,
        resourceUuid: data.resourceUuid,
        isNewSession: data.isNewSession,
        openaiSessionId: data.openaiSessionId,
        errorMessage: data.errorMessage,
        agentName: data.agentName,
        transport: data.transport,
      }),
      timestamp: new Date(store.state.app.serverTime).toISOString(),
    };
    userActivityTrack.track(realtimeSessionEventData as RealtimeSessionEvent);
  },
};
